'use client';

import { ExternalLink } from 'lucide-react';
import { ValeAIMark } from '@/components/icons/vale-ai-mark';
import { cn } from '@/lib/utils';
import { INTAKE_SECTION_LABELS } from '@/lib/constants';
import { sourceFileHref } from './open-source-file';
import {
  formatHolderAttribution,
  type HolderAttribution,
  type QuoteSource
} from '@/lib/services/ai-analysis/types';

/**
 * Subtle, in-card provenance line for an AI-extracted review row (LF-5577).
 *
 * The lawyer is already free to check and edit every extracted entity during
 * review; this doesn't add a decision — it just gives *context* on where a
 * value came from. So it renders as a quiet, left-aligned italic footnote at
 * the foot of the card, subordinate to the data and in the Vale-AI language (a
 * muted sparkle, no status colours), showing the verbatim passage inline (no
 * click needed) with a one-click "View source" for document extractions.
 *
 * Renders nothing when the source is absent or its quote is empty (the wire is
 * best-effort: only AI Scribe validates a non-empty quote; doc-intel / intake
 * pass raw LLM output through, and high-confidence rows often carry no source).
 */

const GENERIC_LABEL: Record<QuoteSource['type'], string> = {
  transcript: 'the meeting transcript',
  document: 'an uploaded document',
  form: 'the intake form'
};

/**
 * Friendly, human-readable label for a typed document source, keyed by the
 * file's `fileType.slug`. Preferred over the raw filename so the footnote reads
 * "Informed by the will" rather than "will-final-v2.pdf". Falls back to the
 * filename (then the generic label) for any slug not listed here. Kept small
 * and obvious on purpose — wording is a light UX call the owner may tweak.
 */
const FRIENDLY_DOCUMENT_LABEL: Record<string, string> = {
  will: 'the will',
  'death-certificate': 'the death certificate',
  'grant-of-representation': 'the grant of representation'
};

/**
 * Resolve a readable section label from an intake form field id. The section is
 * the field id's leading segment (`aboutYou` in `aboutYou.dateOfBirth`); its
 * name comes from the canonical {@link INTAKE_SECTION_LABELS} so this footnote
 * matches the intake Responses tab. Falls back to humanising the raw key for a
 * non-estate / unmapped section rather than leaking a camelCase identifier.
 */
function sectionFromFieldId(fieldId: string): string {
  const sectionKey = fieldId.split('.').filter(Boolean)[0] ?? fieldId;
  const canonical = INTAKE_SECTION_LABELS[sectionKey];
  if (canonical) return canonical;
  const spaced = sectionKey
    .replace(/[_-]/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  if (!spaced) return fieldId;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * How a quote's source reads — `the will` + ` (page 3)`.
 *
 * Exported so a second renderer binds to this derivation rather than restating
 * the two label maps above (VALE-1174: the AI-review change-status popover
 * shows the same source line inside a popover, where this footnote's bordered
 * italic block does not fit). The maps stay private; this is the whole of what
 * a caller needs.
 */
export function describeQuoteSource({
  source,
  sourceFileName,
  sourceFileType
}: {
  source: QuoteSource | undefined;
  sourceFileName?: string;
  sourceFileType?: string;
}): { label: string; suffix: string } {
  // For a document source, prefer a friendly typed-document label (the will /
  // the death certificate / …), then the resolved filename, then the generic
  // fallback when neither resolves.
  const friendlyDocumentLabel =
    source?.type === 'document' && sourceFileType
      ? FRIENDLY_DOCUMENT_LABEL[sourceFileType]
      : undefined;
  const label = !source
    ? ''
    : source.type === 'document'
      ? (friendlyDocumentLabel ?? sourceFileName ?? GENERIC_LABEL.document)
      : GENERIC_LABEL[source.type];
  // Human-readable "where in the source" hint next to the label.
  // A persisted variant's provenance keeps the source type and quote but not the
  // originating `fieldId` (LF-5679), so guard on it — without this an intake-sourced
  // identity variant renders "the intake form ()" with an empty section.
  const suffix =
    source?.type === 'form' && source.fieldId
      ? ` (${sectionFromFieldId(source.fieldId)})`
      : source?.type === 'document' && typeof source.page === 'number'
        ? ` (page ${source.page})`
        : '';

  return { label, suffix };
}

interface ProvenanceAttributionLineProps {
  /** A source already known to be usable — the caller owns the empty-quote gate. */
  source: QuoteSource;
  sourceFileName?: string;
  sourceFileType?: string;
  /** Opens the source document in a new tab (see openSourceFile). */
  onViewSource?: (fileId: number, page?: number) => void;
  /** Opens the source document in the host's own drawer. Wins over onViewSource. */
  onOpenDocument?: (fileId: number, page?: number) => void;
  /**
   * How the line breaks when it runs out of room.
   *
   * `inline` is the footnote's band: the link sits beside the attribution and
   * only drops below it when the row genuinely fills. That is right across a
   * full-width card and wrong inside a `w-80` popover, where the two almost
   * never fit together — the label wraps mid-phrase, the link lands under a
   * ragged edge, and the whole thing reads as a broken paragraph rather than a
   * line. `stacked` gives the link its own row unconditionally, so the narrow
   * case has one predictable shape instead of a width-dependent one.
   *
   * This is a wrapping rule, not a second shape: same words, same mark, same
   * affordance, same order.
   */
  layout?: 'inline' | 'stacked';
  className?: string;
}

/**
 * "Informed by <the will> (page 3)", the Vale AI mark, and the way in.
 *
 * **This is the one definition of how the review says where something came
 * from** (VALE-1417). It was inline in the footnote below, and the AI-review
 * hover card had grown a second, differently-shaped answer to the same question
 * on the same screen: a `Source for this contact` heading over the passage, with
 * the document, the page and the link trailed off the end of a muted sentence.
 * The footnote's shape won on two counts — it leads with the document rather
 * than a heading restating what the reader already knows they are hovering, and
 * it gives the link its own affordance. So the popover binds to this rather than
 * restating it, and the two surfaces cannot drift again.
 *
 * The mark travels WITH the line, not with the footnote's chrome: it is what
 * marks the sentence as the AI accounting for itself rather than as part of the
 * record, which is as true inside a hover card as under a section heading.
 */
export function ProvenanceAttributionLine({
  source,
  sourceFileName,
  sourceFileType,
  onViewSource,
  onOpenDocument,
  layout = 'inline',
  className
}: ProvenanceAttributionLineProps) {
  const { label, suffix } = describeQuoteSource({
    source,
    sourceFileName,
    sourceFileType
  });

  return (
    <div
      className={cn(
        'flex gap-x-2 gap-y-0.5',
        layout === 'stacked'
          ? 'flex-col items-start'
          : 'flex-wrap items-center',
        className
      )}
    >
      {/*
        `items-start` above rather than `items-center`: once the attribution can
        wrap to two lines, centring it against the link centres a two-line block
        against a one-line one and pulls the mark off the first line.
      */}
      <span className="inline-flex items-baseline gap-1.5">
        {/*
          **The mark is the Vale AI purple** (VALE-1417). It rode at 70% opacity
          in the body's own muted grey, which said "de-emphasised footnote" and
          not "this line is the AI speaking" — and the mark carries no colour of
          its own (it inherits `currentColor`), so grey is what it got. `text-ai`
          is the semantic AI token the rest of the product marks its own work
          with, so the glyph now says whose voice this is at a glance.

          `translate-y-px` because the mark is three filled shapes with air
          around them: sat on a true baseline it reads a hair high against the
          x-height of the text beside it.
        */}
        <ValeAIMark box={12} className="text-ai shrink-0 translate-y-px" />
        <span>
          {/*
           * LF-5602 — provenance is ENTITY-level (one source per card), not
           * per-field, so this must read as *context* on where the entity
           * came from, not a guarantee that every field was lifted verbatim
           * from this one document. "Informed by" keeps that subtle and
           * subordinate; the quote below shows the grounding passage.
           */}
          Informed by{' '}
          <span className="text-foreground/80 font-medium">
            {label}
            {suffix}
          </span>
        </span>
      </span>
      {source.type === 'document' && onOpenDocument ? (
        <button
          type="button"
          className="text-primary hover:text-primary/80 inline-flex cursor-pointer items-center gap-1 font-medium focus:outline-none focus-visible:underline"
          onClick={(event) => {
            event.stopPropagation();
            onOpenDocument(source.fileId, source.page);
          }}
        >
          <ExternalLink className="h-3 w-3" aria-hidden />
          View source
        </button>
      ) : source.type === 'document' && onViewSource ? (
        // A real anchor (not a JS button) so the link is copyable,
        // middle-/cmd-clickable, and "open in new tab"-able. `onViewSource`
        // presence is the consumer's opt-in to offer the affordance; the
        // href itself does the navigating (LF-5577 quote popover).
        <a
          href={sourceFileHref(source.fileId, source.page)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 inline-flex items-center gap-1 font-medium focus:outline-none focus-visible:underline"
        >
          <ExternalLink className="h-3 w-3" aria-hidden />
          View source
        </a>
      ) : null}
    </div>
  );
}

/** The grounding passage, in the treatment the attribution line above sits over. */
export function ProvenanceQuote({
  quote,
  className
}: {
  quote: string;
  className?: string;
}) {
  return (
    <p
      className={cn(
        'border-border/60 border-l-2 pl-2 leading-snug italic',
        className
      )}
    >
      &ldquo;{quote}&rdquo;
    </p>
  );
}

interface ProvenanceFootnoteProps {
  source: QuoteSource | undefined;
  /**
   * Filename for a `document` source, when the card can resolve it from its
   * files — a more useful "where from" than the generic label. Optional.
   */
  sourceFileName?: string;
  /**
   * `fileType.slug` for a `document` source, when the card can resolve it. When
   * it names a known typed document (will / death certificate / grant), the
   * footnote shows a friendly label instead of the raw filename. Optional.
   */
  sourceFileType?: string;
  /**
   * VALE-784 — whose asset the document says this is, when it names more than
   * one person. Rendered above the source line and independently of it: this
   * is the one thing on the card the reviewer must not miss, and it is not a
   * statement about provenance so much as about ownership.
   */
  holderAttribution?: HolderAttribution;
  /** Opens the source document for a `document` quote (see openSourceFile). */
  onViewSource?: (fileId: number, page?: number) => void;
  /**
   * **Open the document inside the app**, the host's own document drawer,
   * instead of the file endpoint in a new tab (VALE-1208).
   *
   * The card path keeps the anchor above: it is copyable and middle-clickable,
   * and there the new tab is the only reader on offer. The AI-review surfaces
   * have a drawer, and a link that leaves the review for a browser tab from
   * inside a drawer that already sits over the review is the wrong reader. Given
   * this, `View source` is a button that calls it, and {@link onViewSource} is
   * not consulted.
   */
  onOpenDocument?: (fileId: number, page?: number) => void;
  className?: string;
}

export function ProvenanceFootnote({
  source,
  sourceFileName,
  sourceFileType,
  holderAttribution,
  onViewSource,
  onOpenDocument,
  className
}: ProvenanceFootnoteProps) {
  /* Held as a value rather than a boolean so the passage below narrows to a
     string — the gate and what it guards are then the same fact. */
  const quote =
    source?.quote && source.quote.trim().length > 0 ? source.quote : undefined;
  const hasSource = !!source && !!quote;
  // VALE-784 — the holder line stands on its own. Gating it on a usable
  // `source` (as the rest of this footnote is) would drop the "not the
  // deceased's" warning on exactly the rows most likely to lack a clean
  // grounding quote: a bare row in a portfolio table.
  const holderLine = holderAttribution
    ? formatHolderAttribution(holderAttribution)
    : null;

  if (!hasSource && !holderLine) {
    return null;
  }

  return (
    <div
      className={cn(
        'text-muted-foreground border-border/60 w-full space-y-1 border-t pt-2.5 text-left text-xs',
        className
      )}
      data-provenance-source={source?.type}
    >
      {!!holderLine && (
        <p
          className="text-foreground/80 font-medium"
          data-holder-attribution={holderAttribution?.heldByDeceased}
        >
          {holderLine}
          {!!holderAttribution?.rationale && (
            // A following sentence, not a dash-joined clause: the holder line
            // already ends in one, and chaining them read as doubled
            // punctuation on the rendered card.
            <span className="text-muted-foreground font-normal">
              {' '}
              {holderAttribution.rationale}
            </span>
          )}
        </p>
      )}
      {hasSource && source && (
        <ProvenanceAttributionLine
          source={source}
          sourceFileName={sourceFileName}
          sourceFileType={sourceFileType}
          onViewSource={onViewSource}
          onOpenDocument={onOpenDocument}
        />
      )}
      {!!source && !!quote && <ProvenanceQuote quote={quote} />}
    </div>
  );
}
